import { Component, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Bot,
  CheckCircle2,
  ExternalLink,
  LifeBuoy,
  MessageCircle,
  RotateCcw,
  Send,
  Sparkles,
  X,
} from "lucide-react";
import { dteApi } from "../lib/api";
import "./AssistantWidget.css";

const PRODUCT_LABELS = {
  web_fiscal: "Web fiscal",
  mobile_fiscal: "App fiscal",
  commercial: "App comercial",
  desktop: "Escritorio",
};

const INTERNAL_ROUTES = new Set([
  "/", "/dashboard", "/configuracion", "/configuracion/guia", "/auto-pruebas", "/factura",
  "/credito-fiscal", "/remision", "/nota-credito", "/nota-debito", "/liquidacion",
  "/doc-contable", "/exportacion", "/sujeto-excluido", "/donacion", "/retencion",
  "/invalidacion", "/contingencia", "/documentos", "/clientes", "/compras", "/libros",
  "/iva", "/alertas", "/importaciones", "/reportes", "/equipo", "/miembros", "/suscripcion",
]);

const QUICK_ACTIONS = [
  "Que necesito para comenzar",
  "Como obtengo el Token MH",
  "Como ejecuto las pruebas",
];

const DESKTOP_QUICK_ACTIONS = [
  "Como funciona la versión local",
  "Como hago un respaldo seguro",
  "Donde encuentro las guias",
];

function textValue(value, fallback = "") {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => textValue(item)).filter(Boolean).join(" ");
  if (value && typeof value === "object") {
    return textValue(value.text || value.title || value.label || value.name || value.message, fallback);
  }
  return fallback;
}

function normalizeSource(source) {
  if (!source || typeof source !== "object") return null;
  const url = textValue(source.url);
  if (!/^https:\/\//i.test(url)) return null;
  return { title: textValue(source.title, "Fuente oficial"), url };
}

function normalizeRoute(route) {
  const value = textValue(route).trim();
  if (!value.startsWith("/")) return null;
  try {
    const parsed = new URL(value, window.location.origin);
    return INTERNAL_ROUTES.has(parsed.pathname) ? `${parsed.pathname}${parsed.search}${parsed.hash}` : null;
  } catch (_) {
    return null;
  }
}

function normalizeAssistantResult(result) {
  const data = result && typeof result === "object" ? result : {};
  return {
    content: textValue(data.answer || data.content, "No recibimos una respuesta legible. Intenta nuevamente."),
    sources: Array.isArray(data.sources) ? data.sources.map(normalizeSource).filter(Boolean) : [],
    routes: Array.isArray(data.routes) ? data.routes.map(normalizeRoute).filter(Boolean) : [],
    suggestions: Array.isArray(data.suggestions) ? data.suggestions.map((item) => textValue(item)).filter(Boolean) : [],
  };
}

function errorText(error) {
  const detail = error?.response?.data?.detail;
  return textValue(detail?.message || detail, "No pudimos consultar la ayuda. Intenta nuevamente.");
}

class AssistantErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="assistant-widget assistant-widget-safe-state">
          <div className="assistant-safe-card">
            <LifeBuoy size={18} />
            <div><strong>La ayuda se reinicio</strong><span>El resto de DTE Flow sigue disponible.</span></div>
            <button type="button" onClick={() => this.setState({ hasError: false })} aria-label="Reintentar ayuda">
              <RotateCcw size={16} />
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AssistantWidgetContent({ product, environment }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const quickActions = product === "desktop" ? DESKTOP_QUICK_ACTIONS : QUICK_ACTIONS;

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  const ask = async (value = message) => {
    const text = textValue(value).trim();
    if (text.length < 2 || loading) return;
    const history = messages.slice(-6).map((item) => ({
      role: item.role === "assistant" ? "assistant" : "user",
      content: textValue(item.content).slice(0, 500),
    })).filter((item) => item.content);
    setMessage("");
    setError("");
    setMessages((current) => [...current, { role: "user", content: text }]);
    setLoading(true);
    try {
      const response = await dteApi.assistantAsk({ message: text, product, environment, page: window.location.pathname, history });
      const result = response?.data && typeof response.data === "object" && !Array.isArray(response.data) ? response.data : response;
      setMessages((current) => [...current, { role: "assistant", ...normalizeAssistantResult(result) }]);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  const openRoute = (route) => {
    const path = normalizeRoute(route);
    if (!path) return;
    navigate(path);
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setMessages([]);
    setError("");
    setMessage("");
  };

  return (
    <div className="assistant-widget" data-testid="assistant-widget">
      {open && (
        <section className="assistant-widget-panel" aria-label="Asistente DTE Flow">
          <header className="assistant-widget-header">
            <div className="assistant-widget-brand">
              <span className="assistant-widget-icon"><Bot size={19} /></span>
              <div>
                <strong>Asistente DTE Flow</strong>
                <small><span className="assistant-online-dot" /> En linea · {PRODUCT_LABELS[product] || "DTE Flow"}</small>
              </div>
            </div>
            <div className="assistant-widget-header-actions">
              <button type="button" className="assistant-icon-button" onClick={reset} aria-label="Nueva conversacion" title="Nueva conversacion"><RotateCcw size={16} /></button>
              <button type="button" className="assistant-icon-button" onClick={() => setOpen(false)} aria-label="Cerrar asistente"><X size={18} /></button>
            </div>
          </header>

          <div className="assistant-widget-trustline"><CheckCircle2 size={14} /> Información guiada y fuentes oficiales</div>

          <div className="assistant-widget-body" role="log" aria-live="polite">
            {messages.length === 0 && (
              <div className="assistant-welcome">
                <div className="assistant-welcome-kicker"><Sparkles size={14} /> Asistencia personalizada</div>
                <h3>Qué necesitas resolver?</h3>
                <p>Te acompano con instrucciones claras para configurar y usar DTE Flow, sin pedirte secretos.</p>
                <small>Nunca escribas contraseñas, tokens o llaves privadas en el chat.</small>
                <div className="assistant-suggestions">
                  {quickActions.map((suggestion) => <button key={suggestion} type="button" onClick={() => ask(suggestion)}>{suggestion}</button>)}
                </div>
              </div>
            )}
            {messages.map((item, index) => (
              <article key={`${item.role}-${index}`} className={`assistant-message assistant-message-${item.role}`}>
                {item.role === "assistant" && <span className="assistant-message-avatar"><Bot size={13} /></span>}
                <div className="assistant-message-content">{textValue(item.content)}</div>
                {item.role === "assistant" && item.sources.length > 0 && (
                  <div className="assistant-sources">
                    <small>Fuentes verificables</small>
                    {item.sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer"><span>{source.title}</span><ExternalLink size={12} /></a>)}
                  </div>
                )}
                {item.role === "assistant" && item.routes.length > 0 && (
                  <div className="assistant-routes">
                    <small>Accesos directos en DTE Flow</small>
                    {item.routes.map((route) => <button key={route} type="button" onClick={() => openRoute(route)}><span>{route === "/configuracion/guia" ? "Abrir guia paso a paso" : `Abrir ${route.replace(/^\//, "")}`}</span><ArrowUpRight size={13} /></button>)}
                  </div>
                )}
                {item.role === "assistant" && item.suggestions.length > 0 && (
                  <div className="assistant-followups">
                    {item.suggestions.map((suggestion) => <button key={suggestion} type="button" onClick={() => ask(suggestion)}>{suggestion}</button>)}
                  </div>
                )}
              </article>
            ))}
            {loading && <div className="assistant-loading"><span /><span /><span /> Revisando la guía y las fuentes seguras...</div>}
            {error && <div className="assistant-error" role="alert">{textValue(error)}</div>}
            <div ref={endRef} />
          </div>

          <form className="assistant-widget-form" onSubmit={(event) => { event.preventDefault(); ask(); }}>
            <input value={message} onChange={(event) => setMessage(event.target.value.slice(0, 1200))} placeholder="Escribe tu pregunta..." aria-label="Pregunta para el asistente" maxLength={1200} />
            <button type="submit" disabled={message.trim().length < 2 || loading} aria-label="Enviar pregunta"><Send size={16} /></button>
          </form>
        </section>
      )}
      <button type="button" className="assistant-launcher" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-label="Abrir ayuda DTE Flow">
        {open ? <X size={20} /> : <MessageCircle size={20} />}<span>{open ? "Cerrar" : "Ayuda"}</span>
      </button>
    </div>
  );
}

function AssistantWidget({ product = "web_fiscal", environment = "any" }) {
  return <AssistantErrorBoundary><AssistantWidgetContent product={product} environment={environment} /></AssistantErrorBoundary>;
}

export default AssistantWidget;
